import React from 'react';
import { CheckCircle, AlertCircle, RefreshCw, X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { Button } from './Button';

export interface AnalysisStatus {
  type: 'comprehensive' | 'jd_cv';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message: string;
  retryCount?: number;
  onRetry?: () => void;
  onDismiss?: () => void;
}

interface AnalysisStatusMessageProps {
  comprehensiveStatus?: AnalysisStatus;
  jdCvStatus?: AnalysisStatus;
  className?: string;
}

export const AnalysisStatusMessage: React.FC<AnalysisStatusMessageProps> = ({
  comprehensiveStatus,
  jdCvStatus,
  className = ''
}) => {
  const renderStatusIcon = (status: AnalysisStatus['status']) => {
    switch (status) {
      case 'pending':
        return <LoadingSpinner size="sm" variant="ai" className="text-blue-500" />;
      case 'processing':
        return <LoadingSpinner size="sm" variant="ai" className="text-blue-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const renderStatusMessage = (status: AnalysisStatus, type: 'comprehensive' | 'jd_cv') => {
    const typeLabel = type === 'comprehensive' ? 'Comprehensive' : 'JD vs CV';
    
    return (
      <div className={`flex items-center justify-between p-3 rounded-lg border backdrop-blur-sm ${
        status.status === 'completed' 
          ? 'bg-green-50/80 border-green-200 dark:bg-green-900/20 dark:border-green-800'
          : status.status === 'failed'
          ? 'bg-red-50/80 border-red-200 dark:bg-red-900/20 dark:border-red-800'
          : 'bg-blue-50/80 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
      } ${className}`}>
        <div className="flex items-center space-x-3">
          {renderStatusIcon(status.status)}
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {status.status === 'pending' && `🔄 ${typeLabel} analysis in progress...`}
              {status.status === 'processing' && `🔄 ${typeLabel} analysis in progress...`}
              {status.status === 'completed' && `✅ ${typeLabel} analysis complete`}
              {status.status === 'failed' && `❌ ${typeLabel} analysis failed`}
            </p>
            {status.message && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {status.message}
              </p>
            )}
            {status.retryCount && status.retryCount > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Retry attempt: {status.retryCount}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {status.status === 'failed' && status.onRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={status.onRetry}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          )}
          {status.status === 'completed' && status.onDismiss && (
            <Button
              size="sm"
              variant="ghost"
              onClick={status.onDismiss}
              className="text-xs"
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!comprehensiveStatus && !jdCvStatus) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {comprehensiveStatus && renderStatusMessage(comprehensiveStatus, 'comprehensive')}
      {jdCvStatus && renderStatusMessage(jdCvStatus, 'jd_cv')}
    </div>
  );
};

export default AnalysisStatusMessage;
